import React, { useEffect, useState } from 'react';
import { api } from '../../api.js';
import PageHeader from '../../components/PageHeader.jsx';
import IconSelect from '../../components/IconSelect.jsx';
import { Icon, SECTION_COLOR } from '../../icons.jsx';

const DATE_RANGES = ['Today', '7 days', '15 days', '30 days', '90 days', '180 days', '365 days'];
const TABLE_TABS = ['Orders', 'Trips', 'Fleet'];
const FLEET_SEGMENTS = ['Drivers', 'Trucks', 'Trailers'];

// Static placeholder data — real fuel price lookup will replace this later.
const FUEL_ROWS = [
  { state: 'AL', city: 'Montgomery', vendor: 'Fuel Stop Montgomery', retail: 5.879, rate: 5.124, savings: 0.755 },
  { state: 'AR', city: 'Little Rock', vendor: 'Fuel Stop Atkins', retail: 5.889, rate: 5.017, savings: 0.872 },
  { state: 'AZ', city: 'Tucson', vendor: 'Fuel Stop Willcox', retail: 5.899, rate: 5.271, savings: 0.628 },
  { state: 'CA', city: 'Barstow', vendor: 'Fuel Stop Olancha', retail: 7.399, rate: 6.987, savings: 0.412 },
  { state: 'CO', city: 'Scott City', vendor: 'Fuel Stop Lamar', retail: 5.729, rate: 5.092, savings: 0.637 },
  { state: 'CT', city: 'New Haven', vendor: 'Fuel Stop New Haven', retail: 6.199, rate: 5.229, savings: 0.970 }
];

function SearchIcon() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>;
}
function ArrowLeft() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
}
function ArrowRight() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
}
function MapPin() {
  return <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 6-9 13-9 13s-9-7-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
}

export default function Dashboard() {
  const [loads, setLoads] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [trucks, setTrucks] = useState(null);
  const [invoices, setInvoices] = useState(null);

  // Placeholder controls — not wired to real filtering/reminders yet.
  const [dateRange, setDateRange] = useState('Today');
  const [safetyReminder, setSafetyReminder] = useState('');
  const [tableTab, setTableTab] = useState('Orders');
  const [fleetSegment, setFleetSegment] = useState('Drivers');

  useEffect(() => {
    api('/dispatch/loads').then(setLoads).catch(() => setLoads([]));
    api('/fleet/drivers').then(setDrivers).catch(() => setDrivers([]));
    api('/fleet/trucks').then(setTrucks).catch(() => setTrucks([]));
    api('/accounting/invoices').then(setInvoices).catch(() => setInvoices([]));
  }, []);

  const loading = loads === null || drivers === null || trucks === null || invoices === null;

  const activeLoads = loads?.filter(l => l.status !== 'delivered').length ?? 0;
  const unassignedLoads = loads?.filter(l => l.status === 'unassigned').length ?? 0;
  const deliveredLoads = loads?.filter(l => l.status === 'delivered').length ?? 0;
  const availableDrivers = drivers?.filter(d => d.status === 'available').length ?? 0;
  const availableTrucks = trucks?.filter(t => t.status === 'available').length ?? 0;
  const fleetUtilization = trucks?.length
    ? Math.round(((trucks.length - availableTrucks) / trucks.length) * 100)
    : 0;
  const outstandingTotal = invoices
    ?.filter(i => i.status !== 'paid')
    .reduce((sum, i) => sum + Number(i.amount || 0), 0) ?? 0;
  const overdueCount = invoices?.filter(i => i.status === 'overdue').length ?? 0;

  const recentLoads = [...(loads || [])]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6);

  const tableColumns = {
    Orders: ['Load #', 'Customer', 'Rate', 'Status'],
    Trips: ['Load #', 'Driver', 'Truck', 'Status'],
    Fleet: ['Unit #', 'Type', 'Assigned to', 'Status']
  };

  return (
    <div>
      <PageHeader
        icon={Icon.dashboard}
        title="Dashboard"
        subtitle="Your operation at a glance"
        color={SECTION_COLOR.dashboard}
        actions={
          <>
            <IconSelect icon={Icon.bell} value={safetyReminder} onChange={e => setSafetyReminder(e.target.value)}>
              <option value="">Safety reminder</option>
              <option value="hos">Check hours of service</option>
              <option value="inspection">Pre-trip inspection due</option>
              <option value="permits">Review expiring permits</option>
            </IconSelect>
            <IconSelect icon={Icon.calendar} value={dateRange} onChange={e => setDateRange(e.target.value)}>
              {DATE_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
            </IconSelect>
          </>
        }
      />

      {loading ? (
        <div className="empty">Loading...</div>
      ) : (
        <>
          <div className="kpi-row">
            <div className="kpi">
              <div className="label">Active loads</div>
              <div className="val">{activeLoads}</div>
              {unassignedLoads > 0 && <div className="sub" style={{color:'var(--red)'}}>{unassignedLoads} unassigned</div>}
            </div>
            <div className="kpi">
              <div className="label">Delivered</div>
              <div className="val">{deliveredLoads}</div>
              <div className="sub">total on record</div>
            </div>
            <div className="kpi">
              <div className="label">Fleet utilization</div>
              <div className="val">{fleetUtilization}%</div>
              <div className="sub">{availableTrucks} of {trucks.length} trucks free</div>
            </div>
            <div className="kpi">
              <div className="label">Drivers available</div>
              <div className="val">{availableDrivers}</div>
              <div className="sub">of {drivers.length} total</div>
            </div>
            <div className="kpi">
              <div className="label">Outstanding</div>
              <div className="val">${outstandingTotal.toLocaleString()}</div>
              {overdueCount > 0 && <div className="sub" style={{color:'var(--red)'}}>{overdueCount} overdue</div>}
            </div>
          </div>

          <div className="dash-grid">
            <div className="panel">
              <h3>Recent loads</h3>
              {recentLoads.length === 0 && <div className="empty">No loads yet — create one from Dispatch.</div>}
              {recentLoads.length > 0 && (
                <table>
                  <thead><tr><th>Load #</th><th>Rate</th><th>Status</th></tr></thead>
                  <tbody>
                    {recentLoads.map(l => (
                      <tr key={l.id}>
                        <td>{l.load_number}</td>
                        <td>${l.rate}</td>
                        <td><span className={`badge ${l.status}`}>{l.status.replace('_',' ')}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="panel">
              <div className="panel-head-row">
                <h3 style={{margin:0}}>Fleet snapshot</h3>
                <div className="segmented">
                  {FLEET_SEGMENTS.map(s => (
                    <button key={s}
                            className={'segmented-btn' + (fleetSegment === s ? ' active' : '')}
                            onClick={() => setFleetSegment(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              {fleetSegment === 'Drivers' && (
                <>
                  <div className="activity-row"><span>Total</span><span>{drivers.length}</span></div>
                  <div className="activity-row"><span>Available</span><span>{availableDrivers}</span></div>
                  <div className="activity-row"><span>On load</span><span>{drivers.length - availableDrivers}</span></div>
                </>
              )}
              {fleetSegment === 'Trucks' && (
                <>
                  <div className="activity-row"><span>Total</span><span>{trucks.length}</span></div>
                  <div className="activity-row"><span>Available</span><span>{availableTrucks}</span></div>
                  <div className="activity-row"><span>In use</span><span>{trucks.length - availableTrucks}</span></div>
                </>
              )}
              {fleetSegment === 'Trailers' && (
                <div className="empty">Trailer data — visit Fleet & drivers for details.</div>
              )}
            </div>
          </div>

          <div className="dash-grid" style={{marginTop:15}}>
            <div className="panel">
              <h3>Accounting</h3>
              <div className="stat-row">
                <span>Outstanding</span>
                <span className="stat-val" style={{color:'var(--amber)'}}>${outstandingTotal.toLocaleString()}</span>
              </div>
              <div className="stat-row">
                <span>Overdue</span>
                <span className="stat-val" style={{color: overdueCount > 0 ? 'var(--red)' : 'var(--text)'}}>{overdueCount}</span>
              </div>
              <div className="stat-row">
                <span>Paid</span>
                <span className="stat-val" style={{color:'var(--green)'}}>
                  ${invoices.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.amount || 0), 0).toLocaleString()}
                </span>
              </div>
              <div className="stat-row">
                <span>Total revenue</span>
                <span className="stat-val" style={{color:'var(--blue)'}}>
                  ${invoices.reduce((s, i) => s + Number(i.amount || 0), 0).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="panel">
              <h3>Pickup &amp; Delivery</h3>
              <div style={{display:'flex', gap:24}}>
                <div>
                  <div className="label" style={{marginBottom:6}}>Today</div>
                  <div className="stat-val" style={{color:'var(--amber)', fontSize:22}}>0/0</div>
                </div>
                <div>
                  <div className="label" style={{marginBottom:6}}>Tomorrow</div>
                  <div className="stat-val" style={{color:'var(--blue)', fontSize:22}}>0/0</div>
                </div>
              </div>
              <div className="sub" style={{marginTop:12}}>Pickups/deliveries scheduled — not yet wired to stop dates.</div>
            </div>
          </div>

          <div className="panel" style={{marginTop:15}}>
            <div className="tabs-row">
              {TABLE_TABS.map(t => (
                <button key={t}
                        className={'tab-btn' + (tableTab === t ? ' active' : '')}
                        onClick={() => setTableTab(t)}>
                  {t}
                </button>
              ))}
            </div>
            <div className="table-search">
              <SearchIcon />
              <input placeholder={`Filter ${tableTab.toLowerCase()}...`} style={{margin:0, border:'none', background:'none'}} />
            </div>
            <table>
              <thead>
                <tr>{tableColumns[tableTab].map(c => <th key={c}>{c}</th>)}</tr>
              </thead>
              <tbody>
                <tr><td colSpan={4} className="empty">No data yet</td></tr>
              </tbody>
            </table>
            <div className="table-footer">
              <button className="page-btn"><ArrowLeft /></button>
              <button className="page-btn"><ArrowRight /></button>
              <select style={{margin:0}} defaultValue="10">
                <option>10</option><option>25</option><option>50</option>
              </select>
            </div>
          </div>

          <div className="dash-grid" style={{marginTop:15, gridTemplateColumns:'1.4fr 1fr'}}>
            <div className="panel">
              <div className="panel-head-row">
                <h3 style={{margin:0}}>Fuel prices &amp; savings</h3>
                <span className="sub">As of {new Date().toLocaleDateString()}</span>
              </div>
              <div className="table-search">
                <SearchIcon />
                <input placeholder="Search by name, city, state..." style={{margin:0, border:'none', background:'none'}} />
              </div>
              <table>
                <thead>
                  <tr><th>State</th><th>City</th><th>Vendor</th><th>Retail</th><th>TruckFlow rate</th><th>Savings</th></tr>
                </thead>
                <tbody>
                  {FUEL_ROWS.map(r => (
                    <tr key={r.state + r.city}>
                      <td style={{color:'var(--blue)', fontWeight:600}}>{r.state}</td>
                      <td>{r.city}</td>
                      <td>{r.vendor}</td>
                      <td><span className="price-chip retail">${r.retail.toFixed(3)}</span></td>
                      <td><span className="price-chip rate">${r.rate.toFixed(3)}</span></td>
                      <td><span className="price-chip savings">${r.savings.toFixed(3)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="table-footer" style={{justifyContent:'space-between'}}>
                <span className="sub">Showing 1 to {FUEL_ROWS.length} of 44 entries</span>
                <div style={{display:'flex', gap:6}}>
                  <button className="page-btn"><ArrowLeft /></button>
                  <button className="page-btn"><ArrowRight /></button>
                </div>
              </div>
              <div className="sub" style={{marginTop:8}}>Prices shown are illustrative placeholders — real fuel price lookup coming soon.</div>
            </div>

            <div className="panel" style={{display:'flex', flexDirection:'column'}}>
              <h3>Asset tracking</h3>
              <div className="map-placeholder">
                <MapPin />
                <div style={{fontWeight:600, marginTop:10}}>Live map coming soon</div>
                <div className="sub" style={{marginTop:4}}>Track trucks and trailers in real time once GPS is connected.</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
